/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { EquipmentsDaoService } from '../equipments-dao.service';
import { Equipment } from 'src/app/_model/equipments';
import {
  DbPersistenceService,
  Query
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { AppConfig } from 'src/app/app.config';
import { EquipmentDbTableDefinition } from './equipment-db-table-definition';
import { LocalEquipmentDbTableDefinition } from './local-equipment-db-table-definition';

@Injectable()
export class EquipmentsDbDaoService implements EquipmentsDaoService {

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private equipmentTableDefinition: EquipmentDbTableDefinition,
    private localEquipmentTableDefinition: LocalEquipmentDbTableDefinition,
  ) { }

  public async saveEquipments(equipments: Equipment[]): Promise<void> {
    try {
      let db = await this.dbPersistenceService.getImsDb();

      var equipmentQueries: Query[] = [];
      for (const equipment of equipments) {
        let equipmentQuery: Query = {
          statement:
            'INSERT OR REPLACE INTO ' +
            (equipment.isLocal() ? this.localEquipmentTableDefinition.tableName : this.equipmentTableDefinition.tableName) +
            '(id, label, databag, branchid, branchlabel, equipmentclass, installationsite)' +
            ' VALUES (?,?,?,?,?,?,?)',
          parameters: [
            Number(equipment.getImsId()),
            equipment.label,
            equipment.databag ? JSON.stringify(equipment.databag) : undefined,
            Number(equipment.branch.getImsId()),
            equipment.branch.label,
            equipment.equipmentclass,
            equipment.installationsite
          ]
        };
        equipmentQueries.push(equipmentQuery);
      }

      await this.dbPersistenceService.executeBatch(db, equipmentQueries);
    } catch (error) {
      this.logger.log(
        'Error saving equipments to db',
        error,
        '-',
        error?.message
      );
    }
  }
  public async getEquipments(): Promise<Equipment[]> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let equipments: Equipment[] = [];

      // Get Synced Equipments
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.equipmentTableDefinition.tableName + ';',
        parameters: null
      });
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        equipments.push(
          Equipment.fromDto({
            id: row['id'],
            secondaryid: undefined,
            label: row['label'],
            branch: {
              id: row['branchid'],
              label: row['branchlabel']
            },
            equipmentclass: row['equipmentclass'],
            installationsite: row['installationsite']
          }, row['databag'] ? JSON.parse(row['databag']) : undefined)
        );
      }

      // Get local generated equipments
      let localData = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.localEquipmentTableDefinition.tableName + ';',
        parameters: null
      });
      for (let i = 0; i < localData.values.length; i++) {
        equipments.push(Equipment.fromDto(localData.values[i]));
      }

      return equipments;
    } catch (error) {
      this.logger.log('Error reading equipment from db', error);
      return undefined
    }
  }

  public async reset() {
    // Do nothing
  }

  public async clear() {
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(this.constructor.name, "truncate table", this.equipmentTableDefinition.tableName)
    await this.dbPersistenceService.truncateTable(db, this.equipmentTableDefinition.tableName);
    this.logger.log(this.constructor.name, "truncate table", this.localEquipmentTableDefinition.tableName)
    await this.dbPersistenceService.truncateTable(db, this.localEquipmentTableDefinition.tableName);
  }

}
